import { inject } from '@angular/core';
import { differenceInHours } from 'date-fns';
import { map, Observable } from 'rxjs';
import { AdminGamesService } from '@admin-panel/infra';
import {
  AdminGameDto,
  AdminGamePlayerDto,
  GamePlayerApplicationDto,
  GetAdminGamesQueryParams,
  GetAdminGamesResponse
} from '@core-http';
import {
  capitalizeFirstLetter,
  DateFormats,
  formatDateToString,
  GamePlayerModel,
  HostDetailsModel,
  LocationModel,
  PlayerApplicationModel
} from '@core';
import { AdminGameQueryResult } from './get-admin-games.query-result';
import { AdminGameTypeFilter, GameVisibility } from '@orlikfy/api-interfaces';

export class GetAdminGamesQueryHandler {
  private readonly adminGamesService: AdminGamesService = inject(AdminGamesService);

  query(
    daysBefore: number,
    gameType: AdminGameTypeFilter | undefined,
    visibility: GameVisibility | undefined,
    region: string | undefined
  ): Observable<AdminGameQueryResult[]> {
    const queryParams: GetAdminGamesQueryParams = { daysBefore, gameType, visibility, region };

    return this.adminGamesService
      .getAllGames(queryParams)
      .pipe(map((response: GetAdminGamesResponse) => this.mapToResult(response)));
  }

  private mapToResult(response: GetAdminGamesResponse): AdminGameQueryResult[] {
    return response.adminGames.map((dto: AdminGameDto) => this.mapGame(dto));
  }

  private mapGame(dto: AdminGameDto): AdminGameQueryResult {
    const location: LocationModel = {
      locationId: dto.location.locationId,
      name: dto.location.name,
      coordinates: {
        latitude: dto.location.coordinates.latitude,
        longitude: dto.location.coordinates.longitude
      },
      address: dto.location.address,
      city: dto.location.city
    };

    const host: HostDetailsModel = {
      hostId: dto.host.hostId,
      displayName: dto.host.displayName,
      thumbnail: dto.host.thumbnail,
      rating: {
        hostRating: dto.host.rating?.hostRating ?? null,
        hostRatingCount: dto.host.rating?.hostRatingCount ?? 0
      }
    };

    const players: GamePlayerModel[] = dto.gamePlayers.map((player: AdminGamePlayerDto) => ({
      playerId: player.playerId,
      userId: player.userId,
      userDisplayName: player.userDisplayName,
      isPaid: player.isPaid,
      userThumbnail: player.userThumbnail,
      isHost: player.isHost,
      playerPreferences: {
        preferredPosition: null,
        preferredLevel: null
      }
    }));

    const playerApplications: PlayerApplicationModel[] =
      dto.playerApplications?.map((app: GamePlayerApplicationDto) => ({
        gamePlayerApplicationId: app.gamePlayerApplicationId,
        userId: app.userId,
        userDisplayName: app.userDisplayName,
        userThumbnail: app.userThumbnail,
        playerPreferences: app.playerPreferences
      })) ?? [];

    return {
      gameId: dto.gameId,
      gameName: dto.name,
      gameDateStart: dto.gameDateStart,
      gameDateEnd: dto.gameDateEnd,
      level: dto.level,
      location,
      host,
      locationType: dto.locationType,
      totalSlots: dto.totalSlots,
      occupiedSlots: dto.occupiedSlots,
      priceAmount: parseFloat(dto.priceAmount),
      description: dto.description,
      gameParticipationStatus: dto.currentUserParticipationStatus,
      visibility: dto.visibility,
      isRecurring: dto.isRecurring,
      paymentType: dto.paymentTypes,
      gameContactPhone: dto.gameContactPhone,
      players,
      playerApplications,
      link: dto.link ?? '',
      cancelledAt: dto.cancelledAt,
      cancellationReason: dto.cancellationReason,
      distanceMeters: dto.distanceMeters,
      name: dto.name,
      formattedCreatedAt: dto.createdAt
        ? `${capitalizeFirstLetter(formatDateToString(dto.createdAt, DateFormats.WeekdayFullDate))}, ${formatDateToString(dto.createdAt, DateFormats.Time)}`
        : '',
      createdBeforeGame: this.calcCreatedBeforeGame(dto.createdAt, dto.gameDateStart)
    };
  }

  private calcCreatedBeforeGame(createdAt: string | undefined, gameDateStart: string): string {
    if (!createdAt) return '';
    const totalHours: number = differenceInHours(new Date(gameDateStart), new Date(createdAt));
    if (totalHours < 0) return '';
    const days: number = Math.floor(totalHours / 24);
    const hours: number = totalHours % 24;
    if (days > 0 && hours > 0) return `${days}d ${hours}h przed`;
    if (days > 0) return `${days}d przed`;
    return `${hours}h przed`;
  }
}
